import { clsx, isiPhone } from 'helpers/utils/HTMLUtils';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import { getStringWithoutSpecialChar } from 'helpers/utils/StringUtils';
import translation from 'locales/translation';
import {
  Button,
  ButtonThemes,
  Checkbox,
  ChevronArrowDownIcon,
  CloseIcon,
  IconButton,
  Input,
  TooltipPosition,
} from 'plataforma-fundacao-componentes';
import React, {
  memo,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';

import './styles.scss';
const rootClassName = 'component-select';

type SelectAcceptedTypes = number | string | boolean;

export type SelectOptionsType<
  T extends SelectAcceptedTypes = SelectAcceptedTypes
> = {
  label: string | number;
  value: T | null;
  action?: ReactNode;
  styles?: React.CSSProperties;
  key?: string;
};

// interface BasicSelectProps<
//   T extends SelectAcceptedTypes = SelectAcceptedTypes
// > {
//   'tooltip-position'?: TooltipPosition;
//   'tooltip-text'?: string;
//   id?: string;
//   className?: string;
//   label?: string;
//   placeholder?: string;
//   error?: boolean;
//   helperText?: string;
//   panelFilter?: boolean;
//   panelFilterPlaceholder?: string;
//   showPanelFooter?: boolean;
//   options?: SelectOptionsType<T>[];
//   noOptionMessage?: string;
//   clearButton?: boolean;
//   loading?: boolean;
//   disabled?: boolean;
//   preSelectUniqueOption?: boolean;
//   readOnly?: boolean;
//   tabIndex?: number;
// }

// export interface SelectProps<T extends SelectAcceptedTypes>
//   extends BasicSelectProps<T> {
//   onChange: (event: T | null) => void;
//   value?: T | null;
//   multiple?: undefined | false;
//   showPanelFooter?: never;
//   multiplePlaceholder?: never;
// }

// export interface MultipleSelectProps<T extends SelectAcceptedTypes[]>
//   extends BasicSelectProps<T extends (infer U)[] ? U : T> {
//   onChange: (event: T) => void;
//   value: T;
//   multiple: true;
//   showPanelFooter?: boolean;
//   noOptionMessage?: string;
//   multiplePlaceholder?: (n: number) => string;
// }

type SelectPostEvent = {
  setPanelOpened: (v: boolean) => void;
};

export interface SelectProps {
  'tooltip-position'?: TooltipPosition;
  'tooltip-text'?: string;
  id?: string;
  className?: string;
  onChange: (value: any, evt: SelectPostEvent) => void;
  value?: number | string | any[] | boolean | null;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  multiple?: boolean;
  panelFilter?: boolean;
  panelFilterPlaceholder?: string;
  bottomPanel?: boolean;
  showPanelFooter?: boolean;
  options?: SelectOptionsType[];
  noOptionMessage?: string;
  multiplePlaceholder?: (number: any) => string;
  clearButton?: boolean;
  loading?: boolean;
  disabled?: boolean;
  preSelectUniqueOption?: boolean;
  readOnly?: boolean;
  tabIndex?: number;
}
function Select(props: SelectProps) {
  const [id] = useState<string>(getUniqueKey());
  const [panelId] = useState<string>(getUniqueKey());
  const [panelOpened, setPanelOpened] = useState<boolean>(false);
  const [filterInput, setFilterInput] = useState<string>('');
  const [fakeSelectFocused, setFakeSelectFocused] = useState<boolean>(false);
  const [inter, setInter] = useState<NodeJS.Timer | null>(null);
  const interRef = useRef<any>(inter);
  interRef.current = inter;

  const getKey = (option: { key?: string; value?: any }) => {
    if (option) {
      if (option.key) return option.key;
      if (option.value && typeof option.value !== 'boolean')
        return option.value;
    }
    return getUniqueKey();
  };

  useEffect(() => {
    const func = (evt: globalThis.MouseEvent) => {
      let aux = evt.target as HTMLElement | null;
      let deveFechar = true;
      while (aux && aux.id !== 'root') {
        if (
          aux &&
          aux.id &&
          aux.id === id &&
          aux.classList.contains(`${rootClassName}-outer`)
        ) {
          deveFechar = false;
          break;
        }
        aux = aux.parentElement;
      }
      if (deveFechar) {
        setPanelOpened(false);
      }
    };
    const onEsc = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') {
        setPanelOpened(false);
        evt.preventDefault();
        evt.stopPropagation();
      }
    };

    if (panelOpened) {
      document.addEventListener('keydown', onEsc);
      document.addEventListener('click', func);
      document.body.append(
        document.querySelector(`#${panelId}`) as HTMLElement
      );
      document
        ?.querySelector(`#${id}`)
        ?.append(document.querySelector(`#${panelId}`) as HTMLElement);
    } else {
      document.removeEventListener('keydown', onEsc);
      document.removeEventListener('click', func);
      setTimeout(() => setFilterInput(''), 400);
    }
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.removeEventListener('click', func);
    };
  }, [id, panelId, panelOpened]);

  useEffect(() => {
    if (interRef.current) {
      clearInterval(interRef.current);
      setInter(null);
    }
    const position = () => {
      const trigger = document.querySelector(`#${id}`) as HTMLElement;
      const panel = document.querySelector(
        `#${panelId}.panel-fade-enter-active, #${panelId}.panel-fade-enter-done, #${panelId}.panel-fade-exit-active`
      ) as HTMLElement;

      if (!trigger || !panel) return;
      const triggerBound = trigger.getBoundingClientRect();
      panel.style.width = `${triggerBound.width}px`;

      const windowHeight = window.innerHeight;
      const panelHeight = panel.getBoundingClientRect().height;
      const fitBottom = panelHeight + triggerBound.bottom < windowHeight;
      const fitTop = triggerBound.top - panelHeight > 0;
      if (fitBottom && !props.bottomPanel) {
        panel.style.top = `${triggerBound.bottom - 16}px`;
        panel.style.left = 'unset';
        panel.style.bottom = 'unset';
        panel.style.borderRadius = '0 0 8px 8px';
        panel.style.boxShadow = '0 0 2px 0 rgba(90, 100, 90, 0.3)';
      } else if (fitTop && !props.bottomPanel) {
        panel.style.top = `${triggerBound.top - panelHeight}px`;
        panel.style.left = 'unset';
        panel.style.bottom = 'unset';
        panel.style.borderRadius = '8px';
        panel.style.boxShadow = '0 0 2px 0 rgba(90, 100, 90, 0.3)';
      } else {
        panel.style.top = 'unset';
        panel.style.left = '0';
        panel.style.bottom = '0';
        panel.style.width = '100%';
        panel.style.borderRadius = '8px 8px 0 0';
        panel.style.boxShadow = '0 -10px 0 5000px rgba(0, 0, 0, 0.5)';
      }
    };
    if (panelOpened) {
      position();
      setInter(setInterval(position, 20));
    }
    return () => {
      if (interRef.current) {
        clearInterval(interRef.current);
        setInter(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelOpened]);

  // const opcoes = JSON.stringify(props.options);
  const opcoes = props.options;
  useEffect(() => {
    if (
      props.preSelectUniqueOption &&
      props.options &&
      props.options.length === 1
    ) {
      if (
        props.multiple &&
        props.value &&
        Array.isArray(props.value) &&
        props.value.length !== 1
      ) {
        props.onChange([props.options[0].value], { setPanelOpened });
      } else if (!props.multiple && props.value !== props.options[0].value) {
        props.onChange(props.options[0].value, { setPanelOpened });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opcoes]);

  const getRightObject = () => (
    <div className={`${rootClassName}-objects`}>
      {props.clearButton &&
      !props.readOnly &&
      ((props.multiple &&
        props.value &&
        Array.isArray(props.value) &&
        props.value.length) ||
        (!props.multiple &&
          (props.value || props.value === 0 || props.value === false))) ? (
        <IconButton
          disabled={props.disabled || false}
          tabIndex={-1}
          className={`${rootClassName}-clear-button`}
          icon={<CloseIcon />}
          onClick={() => {
            if (props.multiple) {
              props.onChange([], { setPanelOpened });
            } else {
              props.onChange(null, { setPanelOpened });
            }
          }}
        />
      ) : undefined}
      <IconButton
        disabled={props.disabled || false}
        tabIndex={-1}
        className={`${rootClassName}-arrow ${rootClassName}-arrow-${
          panelOpened ? 'up' : 'down'
        }`}
        onClick={() => setPanelOpened(!panelOpened)}
        icon={<ChevronArrowDownIcon />}
      />
    </div>
  );

  const getInputProps = () => {
    const p: any = {
      ...props,
      className: clsx([rootClassName, props.className]),
      forceFocus: fakeSelectFocused,
      readOnly: true,
      rightObject: getRightObject(),
      onClick: () => {
        const selectFake = document.querySelector(
          `#${id} .${rootClassName}-select-fake`
        ) as HTMLElement;
        if (!props.multiple && isiPhone() && selectFake) {
          selectFake.focus();
        } else {
          setPanelOpened(!panelOpened);
        }
      },
      onFocus: (evt: any) => {
        const selectFake = document.querySelector(
          `#${id} .${rootClassName}-select-fake`
        ) as HTMLElement;
        if (!props.multiple && isiPhone() && selectFake) {
          selectFake.focus();
        } else {
          evt.target.setSelectionRange(0, 0);
        }
      },
      onKeyDown: (evt: any) => {
        if (evt.keyCode === 32) {
          // Space
          setPanelOpened(!panelOpened);
          evt.preventDefault();
        }
        if (evt.keyCode === 9 && panelOpened) {
          // Tab
          const inputFilter = document.querySelector(
            `#${panelId} .${rootClassName}-panel-input input`
          ) as HTMLInputElement;
          if (inputFilter) {
            inputFilter.focus();
            evt.preventDefault();
          } else {
            const firstOption = document.querySelector(
              `#${id} .${rootClassName}-options div`
            ) as HTMLElement;
            if (firstOption) {
              firstOption.focus();
              evt.preventDefault();
            }
          }
        }
      },
      value:
        props.multiple && Array.isArray(props.value)
          ? props.value.length
            ? props.multiplePlaceholder
              ? props.multiplePlaceholder(props.value.length)
              : ''
            : ''
          : props.options
              ?.filter((opt) => opt.value === props.value)
              .map((op) => op.label)[0],
    };
    delete p.showPanelFooter;
    delete p.bottomPanel;
    delete p.clearButton;
    delete p.multiplePlaceholder;
    delete p.multiple;
    delete p.onChange;
    delete p.panelFilter;
    delete p.panelFilterPlaceholder;
    delete p.options;
    delete p.noOptionMessage;
    delete p.preSelectUniqueOption;
    return p;
  };

  const getMultipleOption = (option: SelectOptionsType) => {
    const checked =
      Array.isArray(props.value) && props.value.indexOf(option.value) > -1;
    const handleChange = () => {
      if (props.readOnly) return;
      if (!props.multiple) return;
      if (!checked) {
        const val = Array.isArray(props.value) ? props.value.slice() : [];
        val.push(option.value);
        props.onChange(val, { setPanelOpened });
      } else {
        const val = Array.isArray(props.value) ? props.value.slice() : [];
        val.splice(val.indexOf(option.value), 1);
        props.onChange(val, { setPanelOpened });
      }
    };
    return (
      <div
        tabIndex={1}
        key={getKey(option)}
        className={`${rootClassName}-multiple-option ${
          props.readOnly ? 'read-only' : ''
        }`}
        onKeyDown={(evt) => {
          const child = evt.currentTarget as HTMLElement;
          const parent = child.parentNode as HTMLElement;
          const index = Array.prototype.indexOf.call(parent.children, child);
          const total = parent.childElementCount;
          switch (evt.key) {
            case 'Tab': // Tab
              if (evt.shiftKey) {
                if (index === 0) {
                  const inputFilter = document.querySelector(
                    `#${panelId} .${rootClassName}-panel-input input`
                  ) as HTMLElement;
                  if (inputFilter) {
                    inputFilter.focus();
                    evt.preventDefault();
                  }
                }
              } else if (index === total - 1) {
                const selectAll = document.querySelector(
                  `#${panelId} ${rootClassName}-footer-button.${rootClassName}-selectAll`
                ) as HTMLElement;
                if (selectAll) {
                  selectAll.focus();
                }
              }
              break;
            case 'Enter': // Enter
              if (!checked) {
                handleChange();
              }
              setPanelOpened(false);
              evt.preventDefault();
              break;
            case ' ': // Space
              handleChange();
              evt.preventDefault();
              break;
            case 'ArrowUp': // Up
              if (index > 0) {
                (
                  parent.children[index - 1] as HTMLElement as HTMLElement
                ).focus();
              } else {
                const inputFilter = document.querySelector(
                  `#${panelId} .${rootClassName}-panel-input input`
                ) as HTMLElement;
                if (inputFilter) inputFilter.focus();
              }
              evt.preventDefault();
              break;
            case 'ArrowDown': // Down
              if (index < total - 1) {
                (parent.children[index + 1] as HTMLElement).focus();
                evt.preventDefault();
              }
              break;
            default:
              break;
          }
        }}
        onClick={handleChange}
      >
        <Checkbox
          value={checked}
          disabled={props.readOnly}
          onChange={() => {
            handleChange();
          }}
        />
        <div className={`${rootClassName}-multiple-option-label`}>
          <label style={option.styles}>{option.label}</label>
          {option.action}
        </div>
      </div>
    );
  };

  const getSingleOption = (option: SelectOptionsType) => {
    if (props.multiple) return;
    const handleChange = () => {
      if (!props.readOnly && props.value !== option.value) {
        setPanelOpened(false);
        props.onChange(option.value, { setPanelOpened });
      }
    };

    return (
      <div
        key={getKey(option)}
        tabIndex={1}
        className={`${rootClassName}-single-option ${
          props.readOnly ? 'read-only' : ''
        }`}
        onClick={handleChange}
        onKeyDown={(evt) => {
          const child = evt.currentTarget as HTMLElement;
          const parent = child.parentNode as HTMLElement;
          const index = Array.prototype.indexOf.call(parent.children, child);
          const total = parent.childElementCount;
          switch (evt.key) {
            case 'Tab': // Tab
              if (evt.shiftKey) {
                if (index === 0) {
                  const inputPanel = document.querySelector(
                    `#${panelId} .${rootClassName}-panel-input input`
                  ) as HTMLElement;
                  if (inputPanel) {
                    inputPanel.focus();
                    evt.preventDefault();
                  }
                }
              }
              break;
            case 'Enter': // Enter
              handleChange();
              setPanelOpened(false);
              evt.preventDefault();
              break;
            case ' ': // Space
              handleChange();
              evt.preventDefault();
              break;
            case 'ArrowUp': // Up
              if (index > 0) {
                (parent.children[index - 1] as HTMLElement).focus();
              } else {
                const el = document.querySelector(
                  `#${panelId} .${rootClassName}-panel-input input`
                ) as HTMLElement;
                if (el) el.focus();
              }
              evt.preventDefault();
              break;
            case 'ArrowDown': // Down
              if (index < total - 1) {
                (parent.children[index + 1] as HTMLElement).focus();
              }
              evt.preventDefault();
              break;
            default:
              break;
          }
        }}
      >
        <label style={option.styles}>{option.label}</label>
        {option.action}
      </div>
    );
  };

  const noOptionsMessage = useMemo(
    () => (
      <div className={`${rootClassName}-no-result`}>
        {props.noOptionMessage}
      </div>
    ),
    [props.noOptionMessage]
  );

  const optionsFiltered = useMemo(() => {
    return (
      props.options?.filter((op) =>
        getStringWithoutSpecialChar(op.label)
          .toLowerCase()
          .match(getStringWithoutSpecialChar(filterInput).toLowerCase())
      ) || []
    );
  }, [props.options, filterInput]);

  return (
    <div id={id} className={`${rootClassName}-outer`}>
      {!props.multiple && isiPhone() && (
        <select
          className={`${rootClassName}-select-fake`}
          value={props.value ? String(props.value) : ''}
          onChange={(evt) =>
            props.onChange(evt.target.value, { setPanelOpened })
          }
          tabIndex={props.tabIndex}
          onFocus={() => setFakeSelectFocused(true)}
          onBlur={() => setFakeSelectFocused(false)}
        >
          <option value='' disabled>
            {translation.components.select.single.fakeOption}
          </option>
          {props.options?.map((opt) => {
            return (
              <option
                key={getKey(opt)}
                value={
                  typeof opt.value !== 'string' ? String(opt.value) : opt.value
                }
              >
                {opt.label}
              </option>
            );
          })}
        </select>
      )}
      <Input {...getInputProps()} />
      <CSSTransition
        in={panelOpened}
        timeout={300}
        classNames='panel-fade'
        unmountOnExit
        onEnter={() => {
          const panel = document.querySelector(`#${panelId}`) as HTMLElement;
          if (window.innerHeight < panel.getBoundingClientRect().bottom) {
            panel.style.top = 'unset';
            panel.style.bottom = '0';
          }
          const firstInput = document.querySelector(
            `#${panelId} .${rootClassName}-panel-input input`
          ) as HTMLElement;

          if (firstInput && !isiPhone()) {
            setTimeout(() => {
              firstInput.focus();
            }, 50);
          } else {
            if (props.multiple) {
              const optionToFocus = document.querySelector(
                `#${panelId} .${rootClassName}-options div`
              ) as HTMLElement;
              if (optionToFocus) {
                setTimeout(() => {
                  optionToFocus.focus();
                }, 50);
              }
            } else {
              const elProps = props.options
                ?.map((opt, index) => {
                  return { ...opt, ind: index };
                })
                .find((opt) => opt.value === props.value);
              const index = elProps ? elProps.ind + 1 : 1;
              const query = `#${panelId} .${rootClassName}-options div:nth-child(${index})`;
              const optionToFocus = document.querySelector(
                query
              ) as HTMLElement;
              if (optionToFocus) {
                setTimeout(() => {
                  optionToFocus.focus();
                }, 50);
              }
              // if (!props && props !== 0) {
            }
          }
        }}
        onExit={() => {
          const inputElement = document.querySelector(
            `#${id} input`
          ) as HTMLElement;
          if (inputElement && !isiPhone()) {
            inputElement.focus();
          }
        }}
      >
        <div
          id={panelId}
          className={`${rootClassName}-panel ${
            props.multiple ? 'multiple' : ''
          }`}
        >
          {props.panelFilter && props.options && !!props.options.length && (
            <div className={`${rootClassName}-panel-input`}>
              <Input
                value={filterInput}
                onChange={(evt) => setFilterInput(evt?.target.value)}
                placeholder={props.panelFilterPlaceholder}
                hideLabelAndHelperText
                onKeyDown={(evt) => {
                  if (
                    (evt.key === 'Tab' && !evt.shiftKey) ||
                    evt.key === 'ArrowDown'
                  ) {
                    const el = document.querySelector(
                      `#${id} .${rootClassName}-options div`
                    ) as HTMLElement;
                    if (el) {
                      el.focus();
                      evt.preventDefault();
                    }
                  }
                }}
              />
            </div>
          )}
          <div className={`${rootClassName}-options scroll-white`}>
            {props.options && props.options.length
              ? optionsFiltered.map((option) => {
                  if (props.multiple) {
                    return getMultipleOption(option);
                  }
                  return getSingleOption(option);
                })
              : noOptionsMessage}
          </div>
          {props.multiple &&
            props.showPanelFooter &&
            props.options &&
            !!props.options.length && (
              <React.Fragment>
                <div className={`${rootClassName}-panel-footer-clearfix`} />
                <div className={`${rootClassName}-panel-footer`}>
                  <Button
                    theme={ButtonThemes.DefaultPrimary}
                    disabled={props.readOnly}
                    className={`${rootClassName}-footer-button`}
                    onClick={() =>
                      props.onChange(
                        optionsFiltered.map((op) => op.value),
                        { setPanelOpened }
                      )
                    }
                    tabIndex={1}
                  >
                    {translation.components.select.selectAll}
                  </Button>
                  <Button
                    tabIndex={1}
                    theme={ButtonThemes.DefaultPrimary}
                    disabled={props.readOnly}
                    className={`${rootClassName}-footer-button`}
                    onClick={() => props.onChange([], { setPanelOpened })}
                  >
                    {translation.components.select.clearAll}
                  </Button>
                </div>
              </React.Fragment>
            )}
        </div>
      </CSSTransition>
    </div>
  );
}

Select.defaultProps = {
  onChange: undefined,
  value: '',
  multiple: false,
  options: [],
  clearButton: true,
  showPanelFooter: true,
  panelFilterPlaceholder: translation.components.select.panelFilterPlaceholder,
  noOptionMessage: translation.components.select.noOptionMessage,
  multiplePlaceholder:
    translation.components.select.multiple.numberOfItems.items,
  preSelectUniqueOption: false,
};

export default memo(Select);
